import React, { useState, useEffect } from 'react';
import ELayersView from 'enums/ELayersView';
import 'components/customComponents/Layers/SelectIngredients.scss';
import { TreeNodeExpanded } from 'business/models/CustomModels';
import { treeIconColors, treeNodeIconsColor } from 'utils/Global';
import { ProteinInfoDTO, ProteinLayerDTO } from 'business/models/Models';
import WizardService from 'business/services/WizardService';
import { TreeService } from 'business/services/TreeService';
import { useRMSelector } from 'redux/hooks';
import { selectSelectedLayerId } from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { IStyle } from 'Styles';
import { InputWithIcon } from 'components/input';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import CustomBreadCrumbs from 'components/customComponents/TreeControl/CustomBreadCrumbs/CustomBreadCrumbs';
import ExperimentService from 'business/services/ExperimentService';
import { getProteinLayerProteinFromualtion } from './utils';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { useLayersContainerContext } from '../Layers/hooks';
import { getTreeNodeIdsByType } from '../Layers/utils';
import { LoadingDiv } from 'components/generic';

interface SelectProteinListProps extends WithTabBackButtonStateProps {
  setContentView: (contentView: ELayersView) => void;
  parentNode: any;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  resetProteinTreeStates: () => void;
  setSelectedProtein: (protein: ProteinLayerDTO) => void;
  selectedProtein: ProteinLayerDTO;
}

export function ProteintItem({ text, onClick, path = '', isProteinHasVariants }) {
  return (
    <div
      id="commandpanel-proteinitem-wrapper"
      style={styles.proteinItemMainContainer}
      onClick={onClick}
    >
      <RMSvgIcon
        id="commandpanel-proteinitem-icon"
        name={isProteinHasVariants ? 'ProteinFormulation_Variants' : 'ProteinFormulation'}
        fill={treeNodeIconsColor}
        style={styles.proteinItemIcon}
      />
      <div id="commandpanel-proteinitem-content" className="flex flex-column">
        <div
          id={`commandpanel-proteinitem-title-${text}`}
          style={styles.proteinItemTitle}
          className="text-align-start item-text-flex-start"
        >
          {text}
        </div>
        <div id={`commandpanel-proteinitem-title-${path}`} style={styles.proteinPath}>
          {path}
        </div>
      </div>
    </div>
  );
}

const treeService = new TreeService();
const wizardService = new WizardService();
const experimentService = new ExperimentService();

function SelectProteinList(props: SelectProteinListProps) {
  const {
    setContentView,
    parentNode,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    resetProteinTreeStates,
    setSelectedProtein,
    selectedProtein,
  } = props;
  const [searchProtein, setSearchProtein] = useState('');
  const [filteredPFs, setFilteredPFs] = useState([] as ProteinInfoDTO[]);
  const [globalPFList, setGlobalPFList] = useState([] as ProteinInfoDTO[]);
  const [crumbs, setCrumbs] = useState([]);
  const [selectedCrumbNode, setSelectedCrumbNode] = useState(null);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const treeNodeId = useRMSelector((state) => state.experiment.expData?.treeNodeId);
  const expId = useRMSelector((state) => state.experiment.expData?.experimentId);
  const experimentPlateTypeId = useRMSelector(
    (state) => state?.experiment?.expData?.info?.containerId,
  );
  const { treeControlState } = useLayersContainerContext();
  const treeNodeProteinFormulationIds = getTreeNodeIdsByType(
    treeControlState.treenodes,
    ETreeNodeTypes.Protein,
  );

  const clickBoxContainer = {
    padding: '1.45% 4.36%',
  };

  const inputStyle = {
    width: '100%',
  };

  useEffect(() => {
    if (selectedCrumbNode) {
      const fetchData = async () => {
        let plateTypeId = experimentPlateTypeId;
        if (!plateTypeId) {
          plateTypeId = await experimentService.getExperimentContainerId(expId);
        }
        const response = await wizardService.getWizardProteins(
          selectedCrumbNode.id,
          plateTypeId,
          true,
        );
        if (response != null) {
          const parentList = await treeService.getDocumentParentNodes(
            selectedCrumbNode.id,
            selectedCrumbNode.type,
            true,
          );
          setCrumbs(parentList);
          setGlobalPFList(response);
        }
      };
      fetchData();
    }
  }, [selectedCrumbNode]);

  useEffect(() => {
    (async () => {
      let _parentnode = cloneDeep(parentNode);
      if (!parentNode) {
        _parentnode = new TreeNodeExpanded();
        await treeService.getParentNodeByTreeNodeId(treeNodeId).then((resp) => {
          _parentnode.id = resp.id;
          _parentnode.type = resp.type;
        });
        setSelectedCrumbNode(_parentnode);
      } else {
        setSelectedCrumbNode(parentNode);
      }
    })();
  }, []);

  useEffect(() => {
    if (!globalPFList) {
      setFilteredPFs([]);
      return;
    }

    setFilteredPFs(
      globalPFList.filter(
        (pf) =>
          treeNodeProteinFormulationIds.has(pf.id) &&
          pf.name.toLowerCase().includes(searchProtein.toLowerCase()),
      ),
    );
  }, [globalPFList, searchProtein, treeNodeProteinFormulationIds]);

  useEffect(() => {
    setHandleOnTabBackPress(() => () => {
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW)();
      resetProteinTreeStates();
    });
  }, [setHandleOnTabBackPress]);

  const selectPF = (pf) => {
    const proteinLayerDetail = selectedProtein;
    const proteinFormulation = getProteinLayerProteinFromualtion(
      proteinLayerDetail,
      pf,
      _selectedLayerId,
    );
    proteinLayerDetail.proteinLayerProteinFormulation = [proteinFormulation];
    setSelectedProtein(proteinLayerDetail);
    setContentView(ELayersView.EDITPROTEIN);
  };

  const changeSearchIngredient = (e) => {
    setSearchProtein(e.target.value);
  };

  const openProteinTreeSidebar = () => {
    setContentView(ELayersView.PROTEINTREEPATH);
  };

  const handleCrumbClick = (crumb) => {
    setSelectedCrumbNode(crumb);
  };

  if (treeControlState?.isLoading) {
    return (
      <div className="m-4">
        <LoadingDiv />
      </div>
    );
  }

  return (
    <div id="commandpanel-selectproteinlist-wrapper" className="select-ingredients-tabs">
      <div
        id="commandpanel-selectproteinlist-breadcrumb-container"
        style={{ padding: '1.45% 4.36%' }}
      >
        <CustomBreadCrumbs
          isProteinControl={false}
          isScreenControl={false}
          treeNodes={undefined}
          breadCrumbs={crumbs}
          onBreadCrumbClick={handleCrumbClick}
          hideTreeNodes={true}
        />
      </div>
      <div
        id="commandpanel-selectproteinlist-searchrow"
        className="flex flex-space-between"
        style={clickBoxContainer}
      >
        <InputWithIcon
          icon={
            <RMSvgIcon
              id="commandpanel-selectproteinlist-search-icon"
              name="Search"
              fill="#313131"
            />
          }
          onChange={changeSearchIngredient}
          value={searchProtein}
          placeholder={'Search here...'}
          containerStyle={inputStyle}
          inputStyle={{ height: '40px' }}
        />
        <div
          id="commandpanel-selectproteinlist-treebutton-container"
          style={{ padding: '1.45%' }}
        >
          <RMCustomButton
            iconProps={{
              id: 'commandpanel-selectproteinlist-folder-button',
              name: 'Folder',
              fill: treeIconColors.Folder,
            }}
            onClick={openProteinTreeSidebar}
          />
        </div>
      </div>
      <div id="commandpanel-selectproteinlist-spacer"></div>
      <div
        id="commandpanel-selectproteinlist-item-container"
        className="ingredients-container"
      >
        {filteredPFs != undefined &&
          filteredPFs.map((pf) => (
            <ProteintItem
              text={pf.name}
              key={pf.id}
              onClick={() => selectPF(pf)}
              path={pf.path}
              isProteinHasVariants={pf.isProteinHasVariants}
            />
          ))}
      </div>
    </div>
  );
}

const styles: IStyle = {
  proteinItemMainContainer: {
    display: 'flex',
    boxSizing: 'border-box',
    borderRadius: 4,
    width: '98%',
    padding: '4px 11px',
    marginTop: 5,
    alignItems: 'center',
    cursor: 'pointer',
  },
  proteinItemIcon: {
    marginRight: 5,
  },
  proteinItemTitle: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  proteinPath: {
    color: '#737373',
  },
};
export default withTabBackButton(SelectProteinList);
